import {PageItem_VideoTileContentBlock} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/story_page/v1/service.types';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {TrackPromotions} from '@aboutyou/tracking';
import {
  ContentBlockVideoAutoPlay,
  ImageRatio,
  RADIUS,
  UnstyledLink,
  gridContainer,
  mediaDesktopAndUp,
  mediaTabletAndUp,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';

export const StoryVideoTile: React.FC<{
  content: PageItem_VideoTileContentBlock;
  streamPosition: number;
  testId?: string;
}> = ({content, streamPosition, testId = 'storyVideoTile'}) => {
  const {ratio, videos, linkTarget, promotionTracker} = content;
  const video = videos?.video;

  if (ratio === undefined || video === undefined) return null;

  const aspectRatio = `${ratio.width}:${ratio.height}` as ImageRatio;
  const linkUrl = linkTarget?.url;

  // width/height ratio - used to cap the tile's width on desktop so a
  // portrait video's height (derived from its width via its own aspect-ratio)
  // never exceeds MAX_DESKTOP_HEIGHT_VH
  const widthToHeightRatio = ratio.width / ratio.height;

  const tile = (
    <ContentBlockVideoAutoPlay
      testId={testId}
      video={{
        sources: video.sources,
        lowSources: video.lowSources,
        description: video.description,
        aspectRatio,
        fallbackImage: {
          url: video.fallbackImage.url,
          description: video.fallbackImage.description,
          aspectRatio,
        },
      }}
      disablePlayPauseControl
      disableMuteUnmuteControl
    />
  );

  return (
    <Wrapper
      data-testid={`${testId}Tracker`}
      promotion={{
        source: TrackingPromotionCreative.GenericPromotion,
        promotionTracker,
      }}
      streamPosition={streamPosition}
    >
      <Frame $widthToHeightRatio={widthToHeightRatio}>
        {linkUrl ? (
          <UnstyledLink
            to={linkUrl}
            aria-label={video.description}
            data-testid={`${testId}Link`}
          >
            {tile}
          </UnstyledLink>
        ) : (
          tile
        )}
      </Frame>
    </Wrapper>
  );
};

const Wrapper = styled(TrackPromotions)`
  @media ${mediaTabletAndUp} {
    ${gridContainer}

    & > * {
      width: 100%;
    }
  }
`;

const MAX_DESKTOP_HEIGHT_VH = 80;

const Frame = styled.div<{$widthToHeightRatio: number}>`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: ${RADIUS.Sm};

  & > a {
    display: block;
    width: 100%;
  }

  // A portrait video spans the full (wide) desktop grid width, which can make the tile taller than the viewport.
  // Capping the width instead of the height keeps the video's full ratio intact (no cropping):
  // the video's own aspect-ratio then derives a shorter height from that narrower width.
  @media ${mediaDesktopAndUp} {
    max-width: ${({$widthToHeightRatio}) =>
      `calc(${MAX_DESKTOP_HEIGHT_VH}vh * ${$widthToHeightRatio})`};
    margin: 0 auto;
  }
`;
